import { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Button,
  Stack,
  Radio,
  RadioGroup,
  TextInput,
  Textarea,
  Text,
  Group,
  Paper,
  CloseButton,
  Divider,
  ScrollArea,
  Box,
  Select
} from "@mantine/core";
import {
  apiRequest,
  Permission,
  useDatadog,
  VojomoLoader
} from "@vojomo-core/shared";
import { usePermissions } from "../hooks/use-permissions";

type ShareAccessModalProps = {
  refId: string;
  refType: "topic" | "source";
  onClose: () => void;
};

type PermissionLevel = "read" | "write";

interface SuggestionAccess {
  type: "user" | "company";
  name: string;
  access: PermissionLevel;
}

export const ShareAccessModal = ({
  refId,
  refType,
  onClose
}: ShareAccessModalProps) => {
  const { permissions, fetchPermissions } = usePermissions(refId, refType);
  const [visibility, setVisibility] = useState<string>("private");
  const [grantedAccess, setGrantedAccess] = useState<SuggestionAccess[]>([]);
  const [isSavingAccess, setIsSavingAccess] = useState<boolean>(false);
  const { trackEvent } = useDatadog();

  const [sugestionsList, setSugestionsList] =
    useState<SuggestionAccess[]>(null);

  const [query, setQuery] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  const filteredSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (q?.length <= 2) return [];

    return sugestionsList
      .filter(
        (item) =>
          !grantedAccess.find(
            (ga) => ga.type === item.type && ga.name === item.name
          )
      )
      .filter((item) => item.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [sugestionsList, grantedAccess, query]);

  const addAccess = (entry: SuggestionAccess) => {
    setGrantedAccess((prev) =>
      prev.includes(entry) ? prev : [...prev, entry]
    );

    setQuery("");
    setShowDropdown(false);
  };

  const usersWithAccess = useMemo(() => {
    return grantedAccess
      .filter((ga) => ga.type === "user")
      .map((ga) => {
        return { name: ga.name, access: ga.access };
      });
  }, [grantedAccess]);

  const companiesWithAccess = useMemo(() => {
    return grantedAccess
      .filter((ga) => ga.type === "company")
      .map((ga) => {
        return {
          name: ga.name,
          access: ga.access
        };
      });
  }, [grantedAccess]);

  const hasAccessListChanged = useMemo(() => {
    if (
      permissions &&
      permissions.users.length === usersWithAccess.length &&
      permissions.companies.length === companiesWithAccess.length &&
      permissions.type === visibility
    ) {
      if (
        usersWithAccess.every(
          (val, i) =>
            val.name === permissions.users[i].name &&
            val.access === permissions.users[i].access
        ) &&
        companiesWithAccess.every(
          (val, i) =>
            val.name === permissions.companies[i].name &&
            val.access === permissions.companies[i].access
        )
      ) {
        return false;
      }
    }

    return true;
  }, [usersWithAccess, companiesWithAccess, visibility]);

  const handleRemoveAccess = (entry: SuggestionAccess) => {
    setGrantedAccess((prev) => prev.filter((item) => item.name !== entry.name));
  };

  const handlePermissionChange = (
    entry: SuggestionAccess,
    newAccess: PermissionLevel
  ) => {
    setGrantedAccess((prev) => {
      const newArr = [...prev];
      const findEntry = newArr.find((i) => i.name === entry.name);

      findEntry.access = newAccess;

      return newArr;
    });
  };

  const handleSave = async () => {
    if (hasAccessListChanged) {
      setIsSavingAccess(true);

      const newPermissions = {
        users: usersWithAccess,
        companies: companiesWithAccess,
        type: visibility
      } as Permission;

      trackEvent("updating_permissions", {
        refId,
        refType,
        users: usersWithAccess,
        companies: companiesWithAccess,
        visibility
      });

      await apiRequest<SuggestionAccess[]>({
        subRoute: "topic",
        path: "/update_permissions",
        method: "POST",
        body: {
          refId,
          refType,
          permissions: newPermissions,
          message: message.trim() || null
        }
      });

      await fetchPermissions();

      setIsSavingAccess(false);
    }

    onClose();
  };

  useEffect(() => {
    if (permissions) {
      setVisibility(permissions.type);

      const usersWithAccess = permissions.users.map((u) => {
        return { type: "user", name: u.name, access: u.access };
      }) as SuggestionAccess[];

      const companiesWithAccess = permissions.companies.map((c) => {
        return { type: "company", name: c.name, access: c.access };
      }) as SuggestionAccess[];

      setGrantedAccess([...usersWithAccess, ...companiesWithAccess]);
    }
  }, [permissions]);

  useEffect(() => {
    const f = async () => {
      const suggestions = await apiRequest<SuggestionAccess[]>({
        subRoute: "topic",
        path: `/suggestions`
      });

      setSugestionsList(suggestions);
    };

    f();
  }, []);

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Share access"
      radius="md"
      centered
      closeOnClickOutside={!isSavingAccess}
      withCloseButton={!isSavingAccess}
      size="md"
    >
      {(isSavingAccess || !permissions) && <VojomoLoader />}
      {!isSavingAccess && permissions && (
        <Stack gap="lg">
          <RadioGroup
            label={
              refType === "topic"
                ? "Who can access this document?"
                : "Who can access this source?"
            }
            value={visibility}
            onChange={(value) => setVisibility(value)}
          >
            <Stack gap="sm">
              {refType === "topic" && (
                <Radio
                  value="public"
                  label={
                    <Stack gap={2}>
                      <Text fw={500}>Public</Text>
                      <Text size="xs" c="dimmed">
                        Anyone with the link can view
                      </Text>
                    </Stack>
                  }
                />
              )}

              <Radio
                value="private"
                label={
                  <Stack gap={2}>
                    <Text fw={500}>Private</Text>
                    <Text size="xs" c="dimmed">
                      Only you can view
                    </Text>
                  </Stack>
                }
              />

              <Radio
                value="specific"
                label={
                  <Stack gap={2}>
                    <Text fw={500}>Specific users/companies</Text>
                    <Text size="xs" c="dimmed">
                      Give access to selected users or organizations
                    </Text>
                  </Stack>
                }
              />
            </Stack>
          </RadioGroup>

          {visibility === "specific" && (
            <Stack gap="sm" pos="relative">
              <Box style={{ position: "relative" }}>
                <TextInput
                  label="Add a user or company"
                  placeholder="Type a name or email…"
                  value={query}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setQuery(value);
                    setShowDropdown(value.trim().length > 0);
                  }}
                  onFocus={() => {
                    if (query.trim().length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowDropdown(false);
                    }, 150);
                  }}
                  radius="md"
                />

                {showDropdown && filteredSuggestions.length > 0 && (
                  <Paper
                    withBorder
                    radius="md"
                    shadow="md"
                    style={{
                      position: "absolute",
                      zIndex: 1000,
                      left: 0,
                      right: 0,
                      top: "100%",
                      marginTop: 4,
                      overflow: "hidden"
                    }}
                  >
                    <ScrollArea.Autosize mah={200}>
                      <Stack gap={0}>
                        {filteredSuggestions.map((suggestion, idx) => (
                          <Box
                            key={suggestion.name}
                            style={{
                              cursor: "pointer",
                              padding: "8px 12px"
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              addAccess(suggestion);
                            }}
                          >
                            <Text size="sm">{suggestion.name}</Text>
                            {idx !== filteredSuggestions.length - 1 && (
                              <Divider my="xs" />
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </ScrollArea.Autosize>
                  </Paper>
                )}
              </Box>
              <Textarea
                label="Message (optional)"
                placeholder="Add a note for the people you're sharing with…"
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                radius="md"
                minRows={2}
                maxRows={4}
                autosize
              />

              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Already has access
                </Text>

                <Paper withBorder radius="md" p="sm">
                  <Stack gap="xs">
                    {grantedAccess.length === 0 ? (
                      <Text size="xs" c="dimmed">
                        Nobody has access yet.
                      </Text>
                    ) : (
                      grantedAccess.map((entry, idx) => (
                        <Stack key={entry.name} gap={0}>
                          <Group justify="space-between" align="center">
                            <Text size="sm">{entry.name}</Text>
                            {refType == "topic" && (
                              <Select
                                size="xs"
                                radius="md"
                                data={[
                                  { value: "read", label: "Read" },
                                  { value: "write", label: "Write" }
                                ]}
                                value={entry.access}
                                onChange={(val) =>
                                  handlePermissionChange(
                                    entry,
                                    val as PermissionLevel
                                  )
                                }
                                allowDeselect={false}
                                w={100}
                              />
                            )}
                            <CloseButton
                              size="sm"
                              aria-label={`Remove access for ${entry}`}
                              onClick={() => handleRemoveAccess(entry)}
                            />
                          </Group>

                          {idx !== grantedAccess.length - 1 && (
                            <Divider my="xs" />
                          )}
                        </Stack>
                      ))
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>

            <Button
              onClick={handleSave}
              disabled={
                !visibility ||
                (visibility === "specific" && grantedAccess.length === 0)
              }
            >
              Save
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
